import { fromAPIRecipe } from '@library/menu-service'
import type { Menu, Recipe } from '@library/menu-service'

export const QUICK_AND_EASY_CATEGORY_ID = 'b6606d9e-2dbb-11ed-a194-022218cc141f'
export const NEW_AND_LIMITED_EDITION_CATEGORY_ID = '56b88b66-7e0b-11ec-a129-02e1cea636b3'

const QUICK_AND_EASY_COUNT = 13
const OTHER_COUNT = 17

const hasImage = (recipe: Recipe) => recipe.images.length > 0

function getCategoryRecipes(menu: Menu, categoryId: string): Recipe[] {
  const category = menu.categories[categoryId]

  if (!category) {
    return []
  }

  return category.recipes
    .map(({ id }) => menu.recipes[id])
    .filter((recipe): recipe is NonNullable<typeof recipe> => Boolean(recipe))
    .map(fromAPIRecipe)
}

function selectQuickAndEasyRecipes(menu: Menu): Recipe[] {
  return (
    getCategoryRecipes(menu, QUICK_AND_EASY_CATEGORY_ID)
      .filter(hasImage)
      // "Cooked by you" is defined as preparationType !== 'heat-only' — a recipe can be tagged
      // Quick & Easy and still be a heat-only ready meal, which belongs exclusively to
      // "Cooked by us" (see deriveCookedByUsRecipes below).
      .filter((recipe) => recipe.preparationType !== 'heat-only')
      .slice(0, QUICK_AND_EASY_COUNT)
  )
}

function selectOtherRecipes(menu: Menu, excludeIds: ReadonlySet<string>): Recipe[] {
  return (
    Object.values(menu.recipes)
      .map(fromAPIRecipe)
      .filter(hasImage)
      .filter((recipe) => !excludeIds.has(recipe.id))
      // Same heat-only exclusion as selectQuickAndEasyRecipes above.
      .filter((recipe) => recipe.preparationType !== 'heat-only')
      .slice(0, OTHER_COUNT)
  )
}

type RecipeSource = 'quickAndEasy' | 'other'

// The Quick & Easy-heavy block sits at the centre (items 13-21) since the carousel's initial
// viewport shows the centre of the 30-item sequence, not the front. That block leads with "other"
// (not the more natural "Quick & Easy, Quick & Easy, other") so item 15 — the exact card the
// carousel's generic middle-snap calculation lands on, on every breakpoint — is Quick & Easy.
const INTERLEAVE_PATTERN: readonly RecipeSource[] = [
  ...Array.from({ length: 4 }, () => ['quickAndEasy', 'other', 'other'] as const).flat(),
  ...Array.from({ length: 3 }, () => ['other', 'quickAndEasy', 'quickAndEasy'] as const).flat(),
  ...Array.from({ length: 3 }, () => ['quickAndEasy', 'other', 'other'] as const).flat(),
]

function interleaveCookedByYouRecipes(quickAndEasy: Recipe[], other: Recipe[]): Recipe[] {
  const queues: Record<RecipeSource, Recipe[]> = {
    quickAndEasy: [...quickAndEasy],
    other: [...other],
  }

  // Drop slots whose queue has already run dry (fewer than 13 Quick & Easy or 17 other
  // recipes available) instead of casting shift()'s undefined into a fake Recipe — showing
  // fewer cards is better than crashing the whole section.
  return INTERLEAVE_PATTERN.map((source) => queues[source].shift()).filter(
    (recipe): recipe is Recipe => recipe !== undefined,
  )
}

export function deriveCookedByYouRecipes(menu: Menu): Recipe[] {
  const quickAndEasy = selectQuickAndEasyRecipes(menu)
  const other = selectOtherRecipes(menu, new Set(quickAndEasy.map((recipe) => recipe.id)))

  return interleaveCookedByYouRecipes(quickAndEasy, other)
}

export function deriveCookedByUsRecipes(menu: Menu): Recipe[] {
  return Object.values(menu.recipes)
    .map(fromAPIRecipe)
    .filter((recipe) => recipe.preparationType === 'heat-only')
    .filter(hasImage)
}

export function isNewAndLimitedEdition(menu: Menu, recipeId: string): boolean {
  const category = menu.categories[NEW_AND_LIMITED_EDITION_CATEGORY_ID]

  return category?.recipes.some(({ id }) => id === recipeId) ?? false
}
